import { Node, mergeAttributes } from "@tiptap/core";

// A panel: a boxed block of the content - a note, a result, a warning, an example - drawn by the page's own styles
// (examin-theme.css), never by the markup that carries it. Only the kind of panel is stored (data-panel), so a panel looks
// the same wherever it is shown, follows the page's direction, and cannot bring styles or scripts in with it.
//
// A row of panels (data-panel-row) puts two to four of them side by side - the three coefficient cards of a worked
// example - and falls to one under the other on a narrow screen. How many is not stored: the row spreads what it holds.
export const PANEL_VARIANTS = ["note", "result", "warning", "example"] as const;
export type PanelVariant = (typeof PANEL_VARIANTS)[number];

export const normalizePanelVariant = (value: unknown): PanelVariant =>
  (PANEL_VARIANTS as readonly string[]).includes(value as string) ? (value as PanelVariant) : "note";

export const Panel = Node.create({
  name: "panel",
  group: "block",
  // Text and the blocks made of it. Not another panel, a diagram or a graph: a panel is a box round a few words.
  content: "(paragraph | heading | bulletList | orderedList | codeBlock | table)+",
  defining: true,

  addAttributes() {
    return {
      variant: {
        default: "note",
        parseHTML: (el: HTMLElement) => normalizePanelVariant(el.getAttribute("data-panel")),
        renderHTML: (attrs: { variant: string }) => ({ "data-panel": normalizePanelVariant(attrs.variant) }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-panel]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes), 0];
  },
});

export const PanelRow = Node.create({
  name: "panelRow",
  group: "block",
  content: "panel{2,4}",
  defining: true,

  parseHTML() {
    return [{ tag: "div[data-panel-row]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-panel-row": "" }), 0];
  },
});
